import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingHeroBg,
  colorLandingLine,
  colorLandingSurface,
  colorTextError,
} from "src/design/tokens";
import { SectionHeading } from "./components/SectionHeading";
import { PricingPlanCard } from "./components/PricingPlanCard";
import { LandingButton } from "./components/LandingButton";
import { useSectionNavigation } from "./useSectionNavigation";
import { useGetSubscriptionTiers } from "src/data/landing/queries/subscriptionTiers";

/**
 * Pricing built from the live subscription catalog: one card per tier in
 * priority order, followed by the multi-venue enquiry banner.
 */
export const PricingSection = () => {
  const { t } = useTranslation();
  const scrollToSection = useSectionNavigation();
  const { data: tiers, isPending, isError } = useGetSubscriptionTiers();

  const orderedTiers = [...(tiers ?? [])].sort((a, b) => a.priority - b.priority);

  return (
    <Box
      component="section"
      id="tarifs"
      sx={{ py: { xs: 8, md: 11.5 }, borderTop: `1px solid ${colorLandingLine}` }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          title={t("landingPage.pricing.title")}
          subtitle={t("landingPage.pricing.subtitle")}
        />

        {isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isError || orderedTiers.length === 0 ? (
          <Typography
            sx={{ color: colorTextError, textAlign: "center", py: 8 }}
          >
            {t("landingPage.subscriptionTiers.loadError")}
          </Typography>
        ) : (
          <Grid container spacing={3} sx={{ mt: 3 }} alignItems="stretch">
            {orderedTiers.map((tier) => (
              <Grid item xs={12} md={6} key={tier.name}>
                <PricingPlanCard
                  tier={tier}
                  onSelect={() => scrollToSection("demo")}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <Box
          sx={{
            mt: 3.75,
            backgroundColor: colorLandingHeroBg,
            borderRadius: "24px",
            p: { xs: 3, md: 5 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 3.25,
            color: colorLandingSurface,
          }}
        >
          <Box>
            <Typography
              component="h3"
              sx={{
                color: colorLandingSurface,
                fontSize: { xs: 20, md: 26 },
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              {t("landingPage.pricing.multi.title")}
            </Typography>
            <Typography
              sx={{
                mt: 1,
                maxWidth: "60ch",
                fontSize: 15,
                lineHeight: 1.6,
                color: `${colorLandingSurface}E6`,
              }}
            >
              {t("landingPage.pricing.multi.body")}
            </Typography>
          </Box>
          <LandingButton
            label={t("landingPage.pricing.multi.cta")}
            onClick={() => scrollToSection("demo")}
            sx={{ flexShrink: 0 }}
          />
        </Box>
      </Container>
    </Box>
  );
};
